import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { commonDimensions } from 'utils/constants';
import messages from './messages';
import {
  goldenGate,
  galaxyCinema,
  lotte,
  pizza4ps,
  fvHospital,
  kMarket,
  gs25,
  starbucks,
  anAn,
  iconRestaurant,
  iconCafe,
  iconBar,
  iconHotel,
  iconSupermarket,
  iconFBService,
  iconBuilding,
  iconTrust,
} from './images';

/* ─── Data ────────────────────────────────────────────────────────────────────── */

interface Category {
  key: keyof typeof messages;
  icon: string;
  bgColor: string;
  link: string;
}

const CATEGORIES: Category[] = [
  { key: 'restaurant', icon: iconRestaurant, bgColor: '#fff0e5', link: '#' },
  { key: 'cafe', icon: iconCafe, bgColor: '#fffae9', link: '#' },
  { key: 'bar', icon: iconBar, bgColor: '#f5edff', link: '#' },
  { key: 'hotel', icon: iconHotel, bgColor: '#f0f6fe', link: '#' },
  { key: 'supermarket', icon: iconSupermarket, bgColor: '#ecfdf5', link: '#' },
  { key: 'fbService', icon: iconFBService, bgColor: '#fef2f2', link: '#' },
];

interface Brand {
  name: string;
  logo?: string;
  isMore?: boolean;
}

const BRANDS: Brand[] = [
  { name: 'Golden Gate', logo: goldenGate },
  { name: 'Galaxy Cinema', logo: galaxyCinema },
  { name: 'Lotte Hotels & Resorts', logo: lotte },
  { name: 'Ăn Ăn', logo: anAn },
  { name: 'Pizza 4Ps', logo: pizza4ps },
  { name: 'FV Hospital', logo: fvHospital },
  { name: 'K-Market', logo: kMarket },
  { name: 'GS25', logo: gs25 },
  { name: 'Starbucks', logo: starbucks },
  { name: 'more', isMore: true },
];

/* ─── Chevron SVG ─────────────────────────────────────────────────────────────── */
const ChevronRight: FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.5 2.5L8 6L4.5 9.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── Styled Components ───────────────────────────────────────────────────────── */

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
  @media (max-width: 767px) {
    margin-bottom: 12px;
  }
`;

const Container = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: flex-start;
  @media (max-width: 1023px) {
    flex-direction: column;
    padding: 12px 16px 0;
    gap: 12px;
  }
`;

const CategorySection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 21px;
  min-width: 0;
  @media (max-width: 1023px) {
    width: 100%;
    gap: 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const BuildingIconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  @media (max-width: 1023px) {
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
  margin: 0;
  @media (max-width: 1023px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #6b7280;
  margin: 0;
  @media (max-width: 1023px) {
    color: #4b5563;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 16px;
  padding: 12px 16px;
  gap: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-decoration: none;
  }
  @media (max-width: 1023px) {
    border-radius: 12px;
    padding: 12px;
    gap: 8px;
  }
`;

const CardLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

interface IconCircleProps {
  bgColor: string;
}

const IconCircle = styled.div<IconCircleProps>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  @media (max-width: 1023px) {
    width: 36px;
    height: 36px;
  }
`;

const CategoryIconImg = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  @media (max-width: 1023px) {
    width: 22px;
    height: 22px;
  }
`;

const CategoryLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #111827;
  word-break: break-word;
  @media (max-width: 1023px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const ChevronWrapper = styled.div`
  flex-shrink: 0;
`;

const TrustPanel = styled.div`
  width: 441px;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 1023px) {
    width: 100%;
    padding: 16px;
    gap: 16px;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TrustIconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const TrustText = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #111827;
  @media (max-width: 1023px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 24px;
  @media (max-width: 1023px) {
    gap: 12px;
  }
`;

const BrandLogoWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid #f2f3f5;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 1023px) {
    width: 48px;
    height: 48px;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BrandMoreBadge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid #f2f3f5;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  span {
    font-size: 11px;
    font-weight: 700;
    line-height: 14px;
    color: #3c9041;
    text-align: center;
  }
  @media (max-width: 1023px) {
    width: 48px;
    height: 48px;
    span {
      font-size: 10px;
    }
  }
`;

/* ─── Component ───────────────────────────────────────────────────────────────── */

const BusinessCategorySection: FC = () => {
  return (
    <Wrapper>
      <Container>
        {/* Left: Business Type Categories */}
        <CategorySection>
          <SectionHeader>
            <TitleRow>
              <BuildingIconImg src={iconBuilding} alt="" />
              <SectionTitle>
                <FormattedMessage {...messages.sectionTitle} />
              </SectionTitle>
            </TitleRow>
            <SectionSubtitle>
              <FormattedMessage {...messages.sectionSubtitle} />
            </SectionSubtitle>
          </SectionHeader>

          <CategoryGrid>
            {CATEGORIES.map((cat) => (
              <CategoryCard key={String(cat.key)} to={cat.link}>
                <CardLeft>
                  <IconCircle bgColor={cat.bgColor}>
                    <CategoryIconImg src={cat.icon} alt="" />
                  </IconCircle>
                  <CategoryLabel>
                    <FormattedMessage {...messages[cat.key]} />
                  </CategoryLabel>
                </CardLeft>
                <ChevronWrapper>
                  <ChevronRight />
                </ChevronWrapper>
              </CategoryCard>
            ))}
          </CategoryGrid>
        </CategorySection>

        {/* Right: Trust Panel */}
        <TrustPanel>
          <TrustBadge>
            <TrustIconImg src={iconTrust} alt="" />
            <TrustText>
              <FormattedMessage {...messages.trustBadge} />
            </TrustText>
          </TrustBadge>

          <BrandGrid>
            {BRANDS.map((brand) =>
              brand.isMore ? (
                <BrandMoreBadge key={brand.name}>
                  <span>5,000+</span>
                </BrandMoreBadge>
              ) : (
                <BrandLogoWrapper key={brand.name}>
                  <img src={brand.logo} alt={brand.name} />
                </BrandLogoWrapper>
              ),
            )}
          </BrandGrid>
        </TrustPanel>
      </Container>
    </Wrapper>
  );
};

export default BusinessCategorySection;
